import React, { useEffect, useState, useMemo } from 'react';
import {
  fetchSupplierInvoices,
  createSupplierInvoice,
  updateSupplierInvoice,
  deleteSupplierInvoice,
  getApiBaseUrl,
  fetchPaymentsByInvoice,
  fetchSettings
} from '@/api';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { InvoiceDialog } from './InvoiceDialog';
import { PaymentDialog } from './PaymentDialog';
import { ViewInvoiceDialog } from './ViewInvoiceDialog';
import { EditInvoiceDialog } from './EditInvoiceDialog';
import { AddInvoiceDialog } from './AddInvoiceDialog';
import type { SupplierInvoice, Supplier } from '@/types/supplier';
import { toast } from 'sonner';
import { fetchSuppliers } from '@/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trash2, Download, Calendar as CalendarIcon, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchInvoiceItems } from '@/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, startOfToday, endOfToday, startOfMonth, endOfMonth, subDays, startOfYear, endOfYear } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface Props {
  supplierId: string | null;
}

export function InvoicesTable({ supplierId }: Props) {
  const currencySymbol = useCurrencySymbol();
  const [invoices, setInvoices] = useState<SupplierInvoice[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [current, setCurrent] = useState<SupplierInvoice|undefined>();
  const [editInvoice, setEditInvoice] = useState<SupplierInvoice|null>(null);
  const [payInvoice, setPayInvoice] = useState<SupplierInvoice|null>(null);
  const [viewInvoice, setViewInvoice] = useState<SupplierInvoice|null>(null);
  const [addInvoiceOpen, setAddInvoiceOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [filterSupplier, setFilterSupplier] = useState<string>('All');
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 20 });

  // Load suppliers once
  useEffect(() => {
    fetchSuppliers()
      .then(r => setSuppliers(r.data))
      .catch((err) => {
        console.error('InvoicesTable - Error Fetching Suppliers:', err);
      });
  }, []);

  // Fetch invoices: all invoices if no supplierId, or supplier-specific if supplierId is provided
  useEffect(() => {
    fetchSupplierInvoices(supplierId)
      .then(r => setInvoices(r.data))
      .catch((err) => {
        console.error('InvoicesTable - Fetch Error:', err);
        toast.error(err.message || 'Failed to load invoices');
      });
  }, [supplierId]);

  const reload = () => {
    fetchSupplierInvoices(supplierId)
      .then(r => setInvoices(r.data))
      .catch(() => toast.error('Reload failed'));
  };

  const handleInvoiceSubmit = async (fd: FormData) => {
    try {
      if (current?.id) {
        await updateSupplierInvoice(current.id, fd);
        toast.success('Invoice updated');
      } else {
        await createSupplierInvoice(fd);
        toast.success('Invoice created');
      }
      setDialogOpen(false);
      reload();
    } catch (err: any) {
      toast.error(err.message || 'Save failed');
    }
  };

  const handleDeleteInvoice = async (id: string, invoiceNumber: string) => {
    if (confirm(`Are you sure you want to delete invoice ${invoiceNumber}?`)) {
      try {
        await deleteSupplierInvoice(id);
        toast.success('Invoice deleted');
        reload();
      } catch (err: any) {
        toast.error('Failed to delete invoice');
      }
    }
  };

  const handlePreview = (url: string) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const exportToCSV = () => {
    try {
      // Prepare CSV headers
      const headers = [
        'Invoice Number',
        'Supplier',
        'Date',
        'Due Date',
        'Amount',
        'Paid Amount',
        'Remaining Amount',
        'Status'
      ];

      // Prepare CSV data
      const csvData = filteredInvoices.map(inv => {
        const supplier = suppliers.find(s => s.id === inv.supplierId);
        const remaining = inv.amount - inv.paidAmount;
        
        return [
          `"${inv.invoiceNumber}"`,
          `"${supplier?.name || 'Unknown'}"`,
          `"${inv.date}"`,
          `"${inv.dueDate}"`,
          `"${inv.amount.toFixed(2)}"`,
          `"${inv.paidAmount.toFixed(2)}"`,
          `"${remaining.toFixed(2)}"`,
          `"${inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}"`
        ];
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      
      // Generate filename with current date and filters
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      let filename = `supplier-invoices-${dateStr}`;
      
      // Add filter info to filename
      if (filterStatus !== 'All') {
        filename += `-${filterStatus.toLowerCase()}`;
      }
      if (filterSupplier !== 'All') {
        const supplierName = suppliers.find(s => s.id === filterSupplier)?.name || 'unknown';
        filename += `-${supplierName.toLowerCase().replace(/\s+/g, '-')}`;
      }
      if (filterDateFrom && filterDateTo) {
        filename += `-${filterDateFrom}-to-${filterDateTo}`;
      } else if (filterDateFrom) {
        filename += `-from-${filterDateFrom}`;
      } else if (filterDateTo) {
        filename += `-until-${filterDateTo}`;
      }
      
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${filteredInvoices.length} invoices to CSV`);
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    }
  };

  const generateInvoicePDF = async (invoice: SupplierInvoice) => {
    try {
      // Fetch invoice items and payments in parallel
      const [itemsRes, paymentsRes, settingsRes] = await Promise.all([
        fetchInvoiceItems(invoice.id),
        fetchPaymentsByInvoice(invoice.id),
        fetchSettings()
      ]);

      // Handle nested data structure from API (backend returns { success: true, data: [...] })
      const invoiceItems = itemsRes.data?.data || itemsRes.data || [];
      const payments = paymentsRes.data || [];
      const settings = settingsRes.data;
      const supplier = suppliers.find(s => s.id === invoice.supplierId);

      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 40;
      const headerHeight = 120;
      let cursorY = margin;

      // Header background
      doc.setFillColor(33, 150, 243);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Company name
      doc.setFontSize(24);
      doc.setTextColor(255);
      doc.setFont('helvetica', 'bold');
      doc.text((settings?.company_name || 'Company').toUpperCase(), margin, cursorY + 30);

      // Company details
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      if (settings?.address) {
        doc.text(settings.address, margin, cursorY + 50);
      }
      if (settings?.email || settings?.phone) {
        doc.text(`Email: ${settings?.email || 'N/A'} | Phone: ${settings?.phone || 'N/A'}`, margin, cursorY + 65);
      }

      // Invoice title on right
      doc.setTextColor(255);
      doc.setFontSize(18);
      doc.text('SUPPLIER INVOICE', pageWidth - margin, cursorY + 30, { align: 'right' });

      doc.setFontSize(10);
      doc.text(`Invoice #: ${invoice.invoiceNumber}`, pageWidth - margin, cursorY + 50, { align: 'right' });
      doc.text(`Date: ${invoice.date}`, pageWidth - margin, cursorY + 65, { align: 'right' });
      doc.text(`Due Date: ${invoice.dueDate}`, pageWidth - margin, cursorY + 80, { align: 'right' });

      cursorY = headerHeight + 20;

      // Supplier info section
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40);
      doc.setFontSize(12);
      doc.text('Supplier:', margin, cursorY);
      cursorY += 15;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60);
      doc.setFontSize(10);
      doc.text(supplier?.name || 'Unknown Supplier', margin, cursorY);
      cursorY += 12;
      if (supplier?.email) {
        doc.text(`Email: ${supplier.email}`, margin, cursorY);
        cursorY += 12;
      }
      if (supplier?.phone) {
        doc.text(`Phone: ${supplier.phone}`, margin, cursorY);
        cursorY += 12;
      }
      if (supplier?.address) {
        doc.text(`Address: ${supplier.address}`, margin, cursorY);
        cursorY += 12;
      }
      cursorY += 10;

      // Status badge
      const statusColors: Record<string, [number, number, number]> = {
        pending: [255, 193, 7],
        partial: [33, 150, 243],
        paid: [76, 175, 80]
      };
      const statusColor = statusColors[invoice.status] || [128, 128, 128];
      doc.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
      const statusText = invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1);
      const statusWidth = doc.getTextWidth(statusText) + 20;
      doc.roundedRect(pageWidth - margin - statusWidth, cursorY - 15, statusWidth, 20, 3, 3, 'F');
      doc.setTextColor(255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(statusText, pageWidth - margin - statusWidth / 2, cursorY - 2, { align: 'center' });

      cursorY += 20;

      // Items table
      if (invoiceItems.length > 0) {
        const tableData = invoiceItems.map((item: any) => [
          item.item_name || item.name || 'Unknown Item',
          item.item_sku || item.sku || '-',
          item.quantity.toString(),
          `${currencySymbol}${parseFloat(item.unit_price).toFixed(2)}`,
          `${currencySymbol}${parseFloat(item.total_price).toFixed(2)}`
        ]);

        autoTable(doc, {
          startY: cursorY,
          head: [['Item Name', 'SKU', 'Qty', 'Unit Price', 'Total']],
          body: tableData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10, cellPadding: 8, textColor: 60 },
          headStyles: { fillColor: [33, 150, 243], textColor: 255, fontStyle: 'bold', fontSize: 11 },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          columnStyles: {
            0: { cellWidth: 200 },
            1: { cellWidth: 80 },
            2: { halign: 'center', cellWidth: 50 },
            3: { halign: 'right', cellWidth: 80 },
            4: { halign: 'right', cellWidth: 80 },
          },
        });

        cursorY = (doc as any).lastAutoTable.finalY + 20;
      }

      // Summary section
      const labelX = pageWidth - margin - 120;
      const valueX = pageWidth - margin;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(40);

      doc.text('Subtotal:', labelX, cursorY, { align: 'right' });
      doc.text(`${currencySymbol}${invoice.amount.toFixed(2)}`, valueX, cursorY, { align: 'right' });
      cursorY += 18;

      doc.setTextColor(76, 175, 80);
      doc.text('Paid Amount:', labelX, cursorY, { align: 'right' });
      doc.text(`${currencySymbol}${invoice.paidAmount.toFixed(2)}`, valueX, cursorY, { align: 'right' });
      cursorY += 18;

      const remaining = invoice.amount - invoice.paidAmount;
      if (remaining > 0) {
        doc.setTextColor(244, 67, 54);
        doc.text('Remaining:', labelX, cursorY, { align: 'right' });
        doc.text(`${currencySymbol}${remaining.toFixed(2)}`, valueX, cursorY, { align: 'right' });
        cursorY += 18;
      }

      doc.setFontSize(13);
      doc.setTextColor(33, 150, 243);
      doc.text('TOTAL:', labelX, cursorY + 5, { align: 'right' });
      doc.text(`${currencySymbol}${invoice.amount.toFixed(2)}`, valueX, cursorY + 5, { align: 'right' });

      cursorY += 40;

      // Payments/Transactions table
      if (payments.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40);
        doc.setFontSize(12);
        doc.text('Payment History', margin, cursorY);
        cursorY += 15;

        const paymentData = payments.map((payment: any) => [
          payment.date,
          `${currencySymbol}${parseFloat(payment.amount).toFixed(2)}`,
          payment.notes || '-'
        ]);

        autoTable(doc, {
          startY: cursorY,
          head: [['Date', 'Amount', 'Notes']],
          body: paymentData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10, cellPadding: 8, textColor: 60 },
          headStyles: { fillColor: [76, 175, 80], textColor: 255, fontStyle: 'bold', fontSize: 11 },
          alternateRowStyles: { fillColor: [232, 245, 233] },
          columnStyles: {
            0: { cellWidth: 100 },
            1: { halign: 'right', cellWidth: 100 },
            2: { cellWidth: 'auto' },
          },
        });
      }

      // Footer
      const footerY = pageHeight - 60;
      doc.setDrawColor(200);
      doc.setLineWidth(0.5);
      doc.line(margin, footerY, pageWidth - margin, footerY);

      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text('Thank you for your business!', pageWidth / 2, footerY + 15, { align: 'center' });

      // Save the PDF
      doc.save(`supplier-invoice-${invoice.invoiceNumber}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Filter invoices based on status, date, and supplier
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;

    // Filter by status
    if (filterStatus !== 'All') {
      filtered = filtered.filter(inv => inv.status === filterStatus.toLowerCase());
    }

    // Filter by date range
    if (filterDateFrom) {
      filtered = filtered.filter(inv => inv.date >= filterDateFrom);
    }
    if (filterDateTo) {
      filtered = filtered.filter(inv => inv.date <= filterDateTo);
    }

    // Filter by supplier
    if (filterSupplier !== 'All') {
      filtered = filtered.filter(inv => inv.supplierId === filterSupplier);
    }

    return filtered;
  }, [invoices, filterStatus, filterDateFrom, filterDateTo, filterSupplier]);

  const total = filteredInvoices.length;
  const last_page = Math.max(1, Math.ceil(total / pagination.per_page));
  const paged = useMemo(() => {
    const start = (pagination.current_page - 1) * pagination.per_page;
    return filteredInvoices.slice(start, start + pagination.per_page);
  }, [filteredInvoices, pagination]);

  // Calculate totals for cards based on filtered invoices
  const totals = useMemo(() => {
    const totalAmount = filteredInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    const totalPaid = filteredInvoices.reduce((sum, inv) => sum + inv.paidAmount, 0);
    const totalPayable = filteredInvoices
      .filter(inv => inv.status === 'pending' || inv.status === 'partial')
      .reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0);

    return {
      totalAmount,
      totalPaid,
      totalPayable
    };
  }, [filteredInvoices]);

  return (
    <>
      {/* Cards for Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Total Invoice Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{currencySymbol}{totals.totalAmount.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Total Paid Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{currencySymbol}{totals.totalPaid.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Filtered results</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Total Payable Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{currencySymbol}{totals.totalPayable.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Outstanding balance</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Add Invoice Button */}
      <div className="space-y-4 mb-6">
        {/* First row - Status and Supplier filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Status:</label>
            <select
              className="border rounded px-3 py-2 text-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Partial">Partial</option>
              <option value="Paid">Paid</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Supplier:</label>
            <select
              className="border rounded px-3 py-2 text-sm min-w-[150px]"
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
            >
              <option value="All">All Suppliers</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Second row - Date filters and Add button */}
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      `${format(dateRange.from, 'PP')} - ${format(dateRange.to, 'PP')}`
                    ) : (
                      `${format(dateRange.from, 'PP')} - ...`
                    )
                  ) : (
                    <span>Pick date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="start">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const fromDate = startOfToday();
                      const toDate = endOfToday();
                      setDateRange({ from: fromDate, to: toDate });
                      setFilterDateFrom(format(fromDate, 'yyyy-MM-dd'));
                      setFilterDateTo(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const toDate = endOfToday();
                      const fromDate = subDays(startOfToday(), 6);
                      setDateRange({ from: fromDate, to: toDate });
                      setFilterDateFrom(format(fromDate, 'yyyy-MM-dd'));
                      setFilterDateTo(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    Last 7 days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const toDate = endOfToday();
                      const fromDate = subDays(startOfToday(), 29);
                      setDateRange({ from: fromDate, to: toDate });
                      setFilterDateFrom(format(fromDate, 'yyyy-MM-dd'));
                      setFilterDateTo(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    Last 30 days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const fromDate = startOfMonth(new Date());
                      const toDate = endOfMonth(new Date());
                      setDateRange({ from: fromDate, to: toDate });
                      setFilterDateFrom(format(fromDate, 'yyyy-MM-dd'));
                      setFilterDateTo(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    This month
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const fromDate = startOfYear(new Date());
                      const toDate = endOfYear(new Date());
                      setDateRange({ from: fromDate, to: toDate });
                      setFilterDateFrom(format(fromDate, 'yyyy-MM-dd'));
                      setFilterDateTo(format(toDate, 'yyyy-MM-dd'));
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    This year
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDateRange({});
                      setFilterDateFrom('');
                      setFilterDateTo('');
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range || {});
                    const from = range?.from ? format(range.from, 'yyyy-MM-dd') : '';
                    const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';
                    setFilterDateFrom(from);
                    setFilterDateTo(to);
                    setPagination(prev => ({ ...prev, current_page: 1 }));
                  }}
                  numberOfMonths={2}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilterStatus('All');
                setFilterDateFrom('');
                setFilterDateTo('');
                setDateRange({});
                setFilterSupplier('All');
                setPagination(prev => ({ ...prev, current_page: 1 }));
              }}
            >
              Clear Filters
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={exportToCSV}
              disabled={filteredInvoices.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => setAddInvoiceOpen(true)}>
              Add Invoice
            </Button>
          </div>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Due</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Remaining</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Attachment</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paged.map((inv, idx) => {
            const sup = suppliers.find(s => s.id === inv.supplierId);
            const remaining = inv.amount - inv.paidAmount;
            const index = (pagination.current_page - 1) * pagination.per_page + idx + 1;
            return (
              <TableRow key={inv.id}>
                <TableCell>{index}</TableCell>
                <TableCell>{sup?.name || '—'}</TableCell>
                <TableCell>{inv.date}</TableCell>
                <TableCell>{inv.dueDate}</TableCell>
                <TableCell>{currencySymbol}{inv.amount.toFixed(2)}</TableCell>
                <TableCell>
                  {(inv.status === 'pending' || inv.status === 'partial') && remaining > 0
                    ? `${currencySymbol}${remaining.toFixed(2)}`
                    : '—'}
                </TableCell>
                <TableCell className="capitalize">{inv.status}</TableCell>
                <TableCell>
                  {inv.attachmentPath ? (
                    <Button
                      variant="link"
                      className="text-blue-600 hover:underline p-0"
                      onClick={() => handlePreview(`${getApiBaseUrl()}/${inv.attachmentPath}`)}
                    >
                      View Attachment
                    </Button>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-purple-500 text-purple-500 hover:bg-purple-50"
                      onClick={() => setViewInvoice(inv)}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-blue-500 text-blue-500 hover:bg-blue-50"
                      onClick={() => setEditInvoice(inv)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-green-500 text-green-500 hover:bg-green-50"
                      onClick={() => setPayInvoice(inv)}
                    >
                      Record Payment
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-orange-500 text-orange-500 hover:bg-orange-50"
                      onClick={() => generateInvoicePDF(inv)}
                      title="Download PDF"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-50"
                      onClick={() => handleDeleteInvoice(inv.id, inv.invoiceNumber)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex items-center justify-between py-3">
        <div className="text-sm text-muted-foreground">
          {(() => {
            if (!total) return 'Showing 0-0 of 0 invoices';
            const start = (pagination.current_page - 1) * pagination.per_page + (paged.length ? 1 : 0);
            const end = (pagination.current_page - 1) * pagination.per_page + paged.length;
            return `Showing ${start}-${end} of ${total} invoices`;
          })()}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Per page:</span>
            <Select
              value={String(pagination.per_page)}
              onValueChange={(v) => setPagination(prev => ({ ...prev, per_page: Number(v), current_page: 1 }))}
            >
              <SelectTrigger className="h-8 w-[90px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {last_page > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(p => ({ ...p, current_page: p.current_page - 1 }))}
                disabled={pagination.current_page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(p => ({ ...p, current_page: p.current_page + 1 }))}
                disabled={pagination.current_page >= last_page}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <InvoiceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultValues={current}
        onSubmit={handleInvoiceSubmit}
        suppliers={suppliers}
      />

      <PaymentDialog
        open={!!payInvoice}
        onOpenChange={() => setPayInvoice(null)}
        invoice={payInvoice}
        onCompleted={reload}
      />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Preview Attachment</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4">
            {previewUrl && (
              <div className="w-full">
                {previewUrl.endsWith('.pdf') ? (
                  <iframe
                    src={previewUrl}
                    title="PDF Preview"
                    className="w-full h-[70vh] border rounded"
                  />
                ) : previewUrl.endsWith('.jpg') || previewUrl.endsWith('.jpeg') || previewUrl.endsWith('.png') ? (
                  <img
                    src={previewUrl}
                    alt="Attachment Preview"
                    className="w-full max-h-[70vh] object-contain border rounded"
                  />
                ) : (
                  <p className="text-red-500">Unsupported file type. Please download to view.</p>
                )}
              </div>
            )}
          </div>
          <DialogClose asChild>
            <Button variant="outline" className="mt-4">
              Close
            </Button>
          </DialogClose>
        </DialogContent>
      </Dialog>

      <ViewInvoiceDialog
        open={!!viewInvoice}
        onOpenChange={(open) => !open && setViewInvoice(null)}
        invoice={viewInvoice}
      />

      <EditInvoiceDialog
        open={!!editInvoice}
        onOpenChange={(open) => !open && setEditInvoice(null)}
        invoice={editInvoice}
        onUpdate={reload}
      />

      <AddInvoiceDialog
        open={addInvoiceOpen}
        onOpenChange={setAddInvoiceOpen}
        onSuccess={reload}
        suppliers={suppliers}
      />
    </>
  );
}
